import { useEffect, useState } from "react";

import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { BrushStart, ChartType } from "../../../models/chart_model";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { DynamicDatePicker } from "../../../components/DatePicker";
import { getHaryanaDailyPeak } from "../../../Rest_api/RestApi";
import { HarayanaDailyPeakData } from "../../../models/rajasthan";
import Loading from "../../../components/Loading";
import { stringToConsistentColor } from "../../../extensions/string";

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active: any;
  payload: any;
  label: any;
}) => {
  const fontSize = 12;

  if (active && payload && payload.length) {
    // Extract both slot values from the payload
    const eveningPeakSlot = payload[0]?.payload?._evening_peak_slot;
    const morningPeakSlot = payload[0]?.payload?._morning_peak_slot;

    function formatValues(params: any) {
      return `${parseFloat(params.toString())
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")} `;
    }

    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: "white",
          padding: "8px 10px",
          fontSize: `${fontSize}px`,
          fontWeight: 500,
          borderRadius: "10px",
          boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.1)",
          border: "none",
        }}
      >
        <p
          style={{
            fontSize: `${fontSize + 1}px`,
            color: "#6F6D7C",
            marginBottom: "6px",
            fontWeight: 500,
          }}
        >
          {`${label}`}
        </p>
        {payload.map((entry: any, index: number) => {
          let blockInfo = "";

          // Determine which block info to show based on entry name
          if (entry.name === "Evening Peak" && eveningPeakSlot) {
            blockInfo = ` (Block: ${eveningPeakSlot})`;
          } else if (entry.name === "Morning Peak" && morningPeakSlot) {
            blockInfo = ` (Block: ${morningPeakSlot})`;
          }

          if (entry.name === "Evening Slot" || entry.name === "Morning Slot") {
            return null;
          } else {
            return (
              <p
                key={`item-${index}`}
                style={{
                  color: entry.color,
                  margin: "4px 0",
                  fontWeight: 500,
                }}
              >
                {entry.name === "Evening Peak" ||
                entry.name === "Morning Peak" ||
                entry.name === "Average" ||
                entry.name === "Peak"
                  ? `${entry.name}: ${formatValues(entry.value)} MW${blockInfo}`
                  : `${entry.name}: ${formatValues(entry.value)}`}
              </p>
            );
          }
        })}
      </div>
    );
  }
  return null;
};

const chartSuffixes = [
  { suffix: "peak", displayName: "Peak", stackSuffix: "peak" },
  { suffix: "average", displayName: "Average", stackSuffix: "average" },
  {
    suffix: "evening_peak",
    displayName: "Evening Peak",
    stackSuffix: "evening_peak",
  },
  {
    suffix: "morning_peak",
    displayName: "Morning Peak",
    stackSuffix: "morning_peak",
  },

  {
    suffix: "evening_peak_slot",
    displayName: "Evening Slot",
    stackSuffix: "evening_peak_slot",
  },
  {
    suffix: "morning_peak_slot",
    displayName: "Morning Slot",
    stackSuffix: "morning_peak_slot",
  },
];

const selectedChartType = [
  {
    value: ChartType.Bar,
    label: "Standard Bar",
    stackId: undefined,
  },
  {
    value: ChartType.Line,
    label: "Line",
    stackId: undefined,
  },
];

const DailyPeak = () => {
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [data, setData] = useState<HarayanaDailyPeakData[]>([]);
  const [loading, setLoading] = useState(false);
  const [dates, setDate] = useState({
    startDate: new Date("2025-09-01"),
    endDate: new Date("2025-09-31"),
    key: "selection",
  });

  useEffect(() => {
    async function _handlefetchData() {
      setLoading(true);
      const newData = await getHaryanaDailyPeak(
        dates.startDate.toISOString().split("T")[0],
        dates.endDate.toISOString().split("T")[0]
      );
      setData(newData);
      setLoading(false);
    }

    _handlefetchData();
  }, [dates]);

  function getLegendsName(params: string) {
    if (params === "Evening Peak") {
      return "Evening Peak";
    }
    if (params === "Morning Peak") {
      return "Morning Peak";
    } else {
      return params;
    }
  }

  function getDataKey(key: string, suffix: string) {
    if (suffix === "evening_peak_slot" || suffix === "morning_peak_slot") {
      return `${key}_${suffix}`;
    } else {
      return `${key}_${suffix}_${"MW".toLowerCase()}`;
    }
  }

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    ExchangeColors: string[]
  ) => {
    return {
      dataKey: getDataKey(key, suffix),
      name: getLegendsName(displayName),
      legendColor: undefined,
      type:
        displayName !== "Average"
          ? selectedChartType[0].value
          : selectedChartType[1].value,
      stackId:
        selectedChartType[index].stackId !== undefined
          ? stackSuffix
          : undefined,
    };
  };

  useEffect(() => {
    let filteredData = data;

    if (dates) {
      filteredData = data.filter((item: { date: string }) => {
        const itemDate = new Date(item.date);
        return itemDate >= dates.startDate && itemDate <= dates.endDate;
      });
    }

    const par: ChartDataParams = {
      keys: ["date"],
      groupBy: [],
      volumeKey: ``,
      aggregationKeys: [
        "peak_mw",
        "average_mw",
        "morning_peak_mw",
        "evening_peak_mw",
        "evening_peak_slot",
        "morning_peak_slot",
      ],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [data, dates]);

  return (
    <>
      {loading && <Loading />}
      <div className="dash-card col-span-1 md:col-span-6  h-full w-full">
        <div className="flex justify-between items-center mb-2 ">
          <h2 className="chartHeading">Daily Historic Demand</h2>
          <DynamicDatePicker
            setDate={(date) => setDate(date[0]!)!}
            defaultDates={dates}
            minDate="2019-01-01"
          />
        </div>

        <ReMixChart
          data={chartData}
          showBrush={chartData.length > 20 ? true : undefined}
          brushStart={BrushStart.Start}
          brushIndex={
            chartData.length > 20 ? { startIndex: 0, endIndex: 5 } : undefined
          }
          isTimeSlot={false}
          onlyTitle={true}
          barCategoryGap={10}
          brushHeight={15}
          unit={"MW"}
          yAxisLabel={"MW"}
          legends={chartSuffixes.flatMap(
            ({ suffix, displayName, stackSuffix }) =>
              chartKeys.map(
                (key, index) =>
                  createChartObject(
                    key,
                    index,
                    suffix,
                    displayName,
                    stackSuffix,
                    ExchangeColors
                  ) as any
              )
          )}
          hiddenLegends={["Morning Slot", "Evening Slot"]}
          hiddenStats={["Morning Slot", "Evening Slot"]}
          xDataKey={"date"}
          xTick={undefined}
          secondXDataKey={undefined}
        />
      </div>
    </>
  );
};

export default DailyPeak;
